/**
 * Admin Console → Workspaces: "Enabled features" filter (SaaS only).
 *
 * A multi-select over the six per-workspace feature flags, grouped like the
 * drill-down toggles (task types, autonomy levels).  Picking several flags
 * narrows the list to workspaces that have *every* one of them enabled (AND);
 * nothing picked means no feature filtering.  The trigger shows the first
 * picked flag and a "+N" chip for the rest, like the Data Editor label filter.
 */
import React, { useState } from 'react';
import { DropdownMenu } from '@spark';
import { WORKSPACE_FEATURE_GROUPS, WORKSPACE_FEATURE_LABELS } from '@/shared/domain/workspaceFeatures';

export const FEATURE_FILTER_PLACEHOLDER = 'Filter by enabled features';

/** Whether `workspace` has every feature id in `required` enabled (an empty `required` matches everything). */
export function hasEveryFeature(workspace, required) {
    if (!required?.length) return true;
    const enabled = new Set(Array.isArray(workspace?.features) ? workspace.features : []);
    return required.every((feature) => enabled.has(feature));
}

const ITEMS = WORKSPACE_FEATURE_GROUPS.flatMap((group) => [
    { type: 'section', label: group.title },
    ...group.features.map((feature) => ({ type: 'checkbox', label: feature.label, featureId: feature.id })),
]);

export default function WorkspaceFeatureFilter({ selected, onChange }) {
    const [open, setOpen] = useState(false);
    const selectedLabels = selected.map((id) => WORKSPACE_FEATURE_LABELS[id]).filter(Boolean);

    return (
        <DropdownMenu
            className="admin-feature-filter"
            panelClassName="admin-feature-filter__panel"
            selectType="multi-select"
            size="medium"
            fill
            showLabel={false}
            placeholder={FEATURE_FILTER_PLACEHOLDER}
            open={open}
            onOpenChange={setOpen}
            tags={selectedLabels.slice(0, 1)}
            overflowCount={Math.max(0, selectedLabels.length - 1)}
            onTagRemove={(label) => onChange(selected.filter((id) => WORKSPACE_FEATURE_LABELS[id] !== label))}
            items={ITEMS.map((item) =>
                item.type === 'checkbox' ? { ...item, checked: selected.includes(item.featureId) } : item,
            )}
            onSelect={(item) => {
                if (!item.featureId) return;
                onChange(
                    selected.includes(item.featureId)
                        ? selected.filter((id) => id !== item.featureId)
                        : [...selected, item.featureId],
                );
            }}
        />
    );
}
